"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useBasketContext = exports.basketContext = exports.BasketContextProvider = void 0;
var _basketItem = require("@ori/basket-item");
var _checkoutUtils = require("@ori/checkout-utils");
var _environment = require("@ori/environment");
var _shoppingContext = require("@ori/shopping-context");
var _staticApiClientReact = require("@ori/static-api-client-react");
var _react = require("react");
var _constants = require("../constants");
var _hooks = require("../hooks");
var _logger = require("../logger");
var _jsxRuntime = require("react/jsx-runtime");
const basketContext = exports.basketContext = /*#__PURE__*/(0, _react.createContext)(_constants.defaultBasketContext);
const BasketContextProvider = ({
  shouldMutateCombineSection,
  setShouldMutateCombineSection,
  setIsLoadingAdditionalData,
  children
}) => {
  const {
    shoppingContext
  } = (0, _shoppingContext.useShoppingContext)();
  const {
    tenant
  } = (0, _environment.useEnvironment)();
  const {
    data: {
      isEnabled: isPersonalSubscriptionsEnabled
    } = {
      isEnabled: false
    }
  } = (0, _staticApiClientReact.useGetTenantFeatureToggle)(tenant, _constants.PERSONAL_SUBSCRIPTIONS, {
    swr: (0, _checkoutUtils.getRetryErrorSwrOptions)()
  });
  const {
    translations
  } = (0, _hooks.useTranslations)();
  const basketItemTranslations = (0, _react.useMemo)(() => ({
    ..._basketItem.basketItemDefaultTranslations,
    ...translations
  }), [translations]);
  const basketItemTranslationsNamespace = _constants.translationsNamespace;
  const {
    atpClusterId,
    isAnonymous = false,
    isConsultant = false,
    shoppingMessages = [],
    showBp: showBpGlobally = false,
    unitDataCollection = [],
    shoppingBag
  } = shoppingContext ?? {};
  (0, _logger.logDebug)('shoppingContext: ', shoppingContext);
  const {
    addedItems = [],
    backorderItems = [],
    basketOffers = [],
    isDiscounted = false,
    offers: rawOffers = [],
    reservedItems = [],
    shoppingBagItems = [],
    subscriptions = [],
    vipLoyalty
  } = shoppingContext?.shoppingBag ?? {};
  const offers = (0, _react.useMemo)(() => rawOffers.filter(x => x.offerType !== _constants.SHIPPING_OFFER), [rawOffers]);
  const distinctOffers = (0, _react.useMemo)(() => offers.filter((offer, index) => index === offers.findIndex(innerOffer => innerOffer.choiceId === offer.choiceId)), [offers]);
  const shoppingBagItemsValid = (0, _react.useMemo)(() => shoppingBagItems.filter(x => x.itemType !== 'U'), [shoppingBagItems]);

  // empty basket
  const isEmptyBasket = shoppingBagItemsValid.length === 0 && backorderItems.length === 0 && offers.length === 0;

  // Added sections (free, paid and reserved)
  const added = (0, _react.useMemo)(() => {
    const free = [];
    const paid = [];
    addedItems.forEach(item => {
      if (item.isFree) {
        free.push(item);
      } else {
        paid.push(item);
      }
    });
    paid.push(...reservedItems);
    return {
      free,
      paid
    };
  }, [addedItems, reservedItems]);
  const addedFree = (0, _react.useMemo)(() => added.free, [added.free]);
  const addedPaid = (0, _react.useMemo)(() => [...added.paid, ...reservedItems], [added.paid, reservedItems]);

  // shipping offers
  const shippingOffer = (0, _react.useMemo)(() => rawOffers.find(x => x.offerType === _constants.SHIPPING_OFFER), [rawOffers]);

  // end consumer orders
  const consumerOrders = (0, _react.useMemo)(() => unitDataCollection.find(x => x.unitName === _constants.PENDING_EC_ORDERS), [unitDataCollection]);

  // stuff for banner area
  const vouchers = (0, _react.useMemo)(() => unitDataCollection.filter(x => x.unitName === _constants.VOUCHERS), [unitDataCollection]);
  const message = (0, _react.useMemo)(() => shoppingMessages.find(x => x.hintType === _constants.ADVICE_HINT_TYPE && x.hintCategory === _constants.WELCOME_PROGRAM_CATEGORY), [shoppingMessages]);
  const showBannerSection = Boolean(vipLoyalty) || basketOffers.length > 0 || Boolean(message) || vouchers.length > 0;
  const value = (0, _react.useMemo)(() => ({
    addedFree,
    addedPaid,
    atpClusterId,
    backorderItems,
    basketItemTranslations,
    basketItemTranslationsNamespace,
    basketOffers,
    consumerOrders,
    distinctOffers,
    isAnonymous,
    isConsultant,
    isDiscounted,
    isEmptyBasket,
    isPersonalSubscriptionsEnabled,
    message,
    shoppingBag,
    shoppingBagItems,
    shoppingBagItemsValid,
    shouldMutateCombineSection,
    shippingOffer,
    shoppingMessages,
    showBannerSection,
    showBpGlobally,
    subscriptions,
    vipLoyalty,
    vouchers,
    setIsLoadingAdditionalData,
    setShouldMutateCombineSection
  }), [addedFree, addedPaid, atpClusterId, backorderItems, basketItemTranslations, basketItemTranslationsNamespace, basketOffers, consumerOrders, distinctOffers, isAnonymous, isConsultant, isDiscounted, isEmptyBasket, isPersonalSubscriptionsEnabled, message, shoppingBag, shoppingBagItems, shoppingBagItemsValid, shouldMutateCombineSection, shippingOffer, shoppingMessages, showBannerSection, showBpGlobally, subscriptions, vipLoyalty, vouchers, setIsLoadingAdditionalData, setShouldMutateCombineSection]);
  (0, _logger.logDebug)('basketContext: ', value);
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(basketContext.Provider, {
    value: value,
    children: children
  });
};
exports.BasketContextProvider = BasketContextProvider;
const useBasketContext = () => (0, _react.useContext)(basketContext);
exports.useBasketContext = useBasketContext;